import { useEffect, useRef, useState } from 'react'
import moment from 'moment-timezone'
import API from '../../utils/API'
import { getErrorMessage } from './coachCalendarPreviewUtils'

// Wait after a month click so quick clicks don't each read the coach's external calendars
const MONTH_CHANGE_DEBOUNCE_MS = 300

// Shared data for the read-only calendar previews.
// - Slots re-read on month or timezone change (timezone is sent only after the admin picks one).
// - Events re-read on month change only; times are converted to the selected timezone in the UI.
// - Late replies for an older month/timezone are ignored.
const useCoachCalendarPreview = ({ show, coachId }) => {
  const [selectedMonth, setSelectedMonth] = useState(() =>
    moment().startOf('month'),
  )
  const [debouncedMonth, setDebouncedMonth] = useState(selectedMonth)
  const [selectedDate, setSelectedDate] = useState(null)
  // null until the admin picks a timezone; until then the coach's timezone from the response is used
  const [pickedTimezone, setPickedTimezone] = useState(null)

  const [slotsState, setSlotsState] = useState({
    isLoading: false,
    error: null,
    days: {},
    timezone: null,
  })
  const [eventsState, setEventsState] = useState({
    isLoading: false,
    error: null,
    liveEvents: [],
    timezone: null,
  })
  const [accountsState, setAccountsState] = useState({
    isLoading: false,
    error: null,
    accounts: [],
  })

  const slotsRequestIdRef = useRef(0)
  const eventsRequestIdRef = useRef(0)
  const accountsRequestIdRef = useRef(0)

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedMonth(selectedMonth),
      MONTH_CHANGE_DEBOUNCE_MS,
    )
    return () => clearTimeout(timer)
  }, [selectedMonth])

  // Default selection: today when it is in the shown month, otherwise the 1st
  useEffect(() => {
    const today = moment()
    setSelectedDate(
      today.isSame(debouncedMonth, 'month')
        ? today.format('YYYY-MM-DD')
        : debouncedMonth.clone().startOf('month').format('YYYY-MM-DD'),
    )
  }, [debouncedMonth])

  useEffect(() => {
    if (!show || !coachId) return
    const requestId = ++slotsRequestIdRef.current

    const loadSlots = async () => {
      setSlotsState((prev) => ({ ...prev, isLoading: true, error: null }))
      try {
        const { data } =
          await API.AdminAPI.scheduling.getCoachAvailableSlotsPreview(coachId, {
            year: debouncedMonth.year(),
            month: debouncedMonth.month() + 1,
            timezone: pickedTimezone || undefined,
          })
        if (requestId !== slotsRequestIdRef.current) return

        const days = (data?.days || []).reduce((acc, day) => {
          if (day?.date) acc[day.date] = day
          return acc
        }, {})
        setSlotsState({
          isLoading: false,
          error: null,
          days,
          timezone: data?.timezone || null,
        })
      } catch (error) {
        if (requestId !== slotsRequestIdRef.current) return
        // Keep the error visible; an empty list here would look like a coach with no slots
        setSlotsState({
          isLoading: false,
          error: getErrorMessage(error, 'Could not load open slots.'),
          days: {},
          timezone: null,
        })
      }
    }

    loadSlots()
  }, [show, coachId, debouncedMonth, pickedTimezone])

  useEffect(() => {
    if (!show || !coachId) return
    const requestId = ++eventsRequestIdRef.current

    const loadEvents = async () => {
      setEventsState((prev) => ({ ...prev, isLoading: true, error: null }))
      try {
        const { data } = await API.AdminAPI.scheduling.getCoachCalendarPreview(
          coachId,
          {
            year: debouncedMonth.year(),
            month: debouncedMonth.month() + 1,
          },
        )
        if (requestId !== eventsRequestIdRef.current) return
        setEventsState({
          isLoading: false,
          error: null,
          liveEvents: Array.isArray(data?.liveEvents) ? data.liveEvents : [],
          timezone: data?.timezone || null,
        })
      } catch (error) {
        if (requestId !== eventsRequestIdRef.current) return
        setEventsState({
          isLoading: false,
          error: getErrorMessage(error, 'Could not load calendar events.'),
          liveEvents: [],
          timezone: null,
        })
      }
    }

    loadEvents()
  }, [show, coachId, debouncedMonth])

  useEffect(() => {
    if (!show || !coachId) return
    const requestId = ++accountsRequestIdRef.current

    const loadAccounts = async () => {
      setAccountsState((prev) => ({ ...prev, isLoading: true, error: null }))
      try {
        const { data } = await API.AdminAPI.scheduling.getAllConnectedCalendars(
          coachId,
        )
        if (requestId !== accountsRequestIdRef.current) return
        setAccountsState({
          isLoading: false,
          error: null,
          accounts: Array.isArray(data) ? data : [],
        })
      } catch (error) {
        if (requestId !== accountsRequestIdRef.current) return
        setAccountsState({
          isLoading: false,
          error: getErrorMessage(error, 'Could not load connected calendars.'),
          accounts: [],
        })
      }
    }

    loadAccounts()
  }, [show, coachId])

  // Selected timezone if the admin picked one; otherwise the coach's timezone from the response
  const timezone =
    pickedTimezone || slotsState.timezone || eventsState.timezone || 'UTC'
  const isMonthPending = !selectedMonth.isSame(debouncedMonth, 'month')
  const isBusy = isMonthPending || slotsState.isLoading || eventsState.isLoading

  return {
    selectedMonth,
    setSelectedMonth,
    isMonthPending,
    selectedDate,
    setSelectedDate,
    timezone,
    setPickedTimezone,
    isBusy,
    slotDays: slotsState.days,
    slotsLoading: slotsState.isLoading,
    slotsError: slotsState.error,
    liveEvents: eventsState.liveEvents,
    eventsLoading: eventsState.isLoading,
    eventsError: eventsState.error,
    accounts: accountsState.accounts,
    accountsLoading: accountsState.isLoading,
    accountsError: accountsState.error,
  }
}

export default useCoachCalendarPreview
